"use client";

import { useState } from "react";
import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import {
  EyebrowBadge,
  HeroGlow,
  Input,
  Textarea,
  FormError,
  FormSuccessPage,
  brandActionClasses,
} from "@/components/ui";
import { useFormik } from "formik";
import { fieldTouchedError } from "@/lib/form-helpers";
import { writeForUsSchema } from "@/lib/form-schemas";
import { cn } from "@/lib/utils";
import {
  ArrowRight,
  PenLine,
  CheckCircle2,
  Users,
  BarChart3,
  Globe,
  Zap,
  BookOpen,
  TrendingUp,
  Megaphone,
  Send,
  ChevronRight,
  FileText,
  Upload,
  X,
  Network,
  Shield,
  Database,
  Cpu,
  Layers,
  Wrench,
  Lock,
  Terminal,
  Brain,
  ShieldCheck,
  Code2,
  AlignJustify,
  Microscope,
} from "@/components/icons";

const TOPICS = [
  {
    icon: <BookOpen size={18} />,
    color: "color-mix(in srgb, var(--foreground) 60%, transparent)",
    label: "Guides",
    description:
      "Deep technical walkthroughs on web scraping, automation, and proxy infrastructure.",
  },
  {
    icon: <Zap size={18} />,
    color: "#5b8cff",
    label: "Tutorials",
    description: "Step-by-step code tutorials in Python, Node.js, or other languages.",
  },
  {
    icon: <TrendingUp size={18} />,
    color: "#f59e0b",
    label: "Research",
    description:
      "Original research, benchmarks, or data-backed analysis in the proxy/scraping space.",
  },
  {
    icon: <Globe size={18} />,
    color: "#06b6d4",
    label: "AI & Data",
    description: "Articles about AI data pipelines, LLM training data, and large-scale collection.",
  },
  {
    icon: <Megaphone size={18} />,
    color: "#ec4899",
    label: "Opinion",
    description:
      "Thoughtful takes on the state of web data, privacy, anti-bot tech, and the industry.",
  },
  {
    icon: <BarChart3 size={18} />,
    color: "#f97316",
    label: "Case Studies",
    description: "Real-world examples of how teams solve data collection problems at scale.",
  },
  {
    icon: <Network size={18} />,
    color: "#60a5fa",
    label: "Proxy Technology",
    description: "In-depth coverage of proxy infrastructure, protocols, and architecture.",
  },
  {
    icon: <Shield size={18} />,
    color: "#f59e0b",
    label: "Reliable Access",
    description:
      "Techniques for large-scale public data collection, geolocation testing, and reliable web scraping.",
  },
  {
    icon: <Database size={18} />,
    color: "#34d399",
    label: "Web Scraping",
    description:
      "Data collection strategies, crawling at scale, and structured extraction methods.",
  },
  {
    icon: <Cpu size={18} />,
    color: "#c084fc",
    label: "Web Automation",
    description: "Browser automation, headless workflows, and scripting for repetitive web tasks.",
  },
  {
    icon: <Layers size={18} />,
    color: "#06b6d4",
    label: "Platform Use Cases",
    description:
      "Platform-specific proxy applications across social, e-commerce, and data platforms.",
  },
  {
    icon: <Wrench size={18} />,
    color: "#f97316",
    label: "Troubleshooting",
    description:
      "Diagnosing and resolving technical issues related to proxies and scraping setups.",
  },
  {
    icon: <Lock size={18} />,
    color: "#ec4899",
    label: "Network & Security",
    description:
      "Fundamentals of networking, encryption, and security as they relate to proxy use.",
  },
  {
    icon: <TrendingUp size={18} />,
    color: "#84cc16",
    label: "Business Use Cases",
    description:
      "How businesses and marketers leverage web scraping and proxies to drive decisions.",
  },
  {
    icon: <Terminal size={18} />,
    color: "#22d3ee",
    label: "Dev Tools & Integrations",
    description:
      "Libraries, APIs, and tooling that connect proxy infrastructure with developer workflows.",
  },
];

const REJECTED_IF = [
  "Content is too editorial or vague",
  "Paid promotion",
  "Copies infographics made by others",
  "Syndicated content",
  "Overly AI-generated",
];

const WRITING_EXPECTATIONS = [
  {
    icon: <Brain size={15} />,
    text: "Our audience expects expertise. Vague, generic, or shallow content will not be accepted. Explanations must be specific — avoid broad summaries and keyword stuffing.",
  },
  {
    icon: <FileText size={15} />,
    text: "Submitted content must be in American English spelling. Use simple and clear language. Avoid verbose vocabulary — clarity is expertise.",
  },
  {
    icon: <BookOpen size={15} />,
    text: "Articles should be professional, accessible, dense with value, and free of fluff, hype, or assumptions. Blog and research pieces should not be promotional.",
  },
  {
    icon: <ShieldCheck size={15} />,
    text: "Submitted content should be 100% original and must not have been published elsewhere or AI-generated.",
  },
  {
    icon: <Microscope size={15} />,
    text: "The article should incorporate examples where possible and include relevant screenshots if discussing an app, program, website, etc.",
  },
  {
    icon: <Code2 size={15} />,
    text: "When you use code blocks for commands, scripts, JSON, config files, and outputs, remember to mark the correct language.",
  },
  {
    icon: <AlignJustify size={15} />,
    text: "Aim for ~1,200 words or as long as the topic requires. Every article must include key takeaways (5–6 bullets) and an FAQ.",
  },
  {
    icon: <Layers size={15} />,
    text: "Use subheadings, bullet points, and short, to-the-point paragraphs to enhance readability.",
  },
];

const PERKS = [
  {
    icon: <Users size={20} />,
    title: "Reach 50,000+ developers",
    body: "Our blog is read by engineers, data scientists, and technical teams working on real scraping and proxy challenges.",
  },
  {
    icon: <Globe size={20} />,
    title: "A do-follow backlink",
    body: "Each published article includes a do-follow author bio link back to your site, tool, or GitHub profile.",
  },
  {
    icon: <BarChart3 size={20} />,
    title: "Shared on our socials",
    body: "Accepted posts are promoted across our LinkedIn, X, and newsletter to thousands of subscribers.",
  },
  {
    icon: <PenLine size={20} />,
    title: "Editorial support",
    body: "Our editing team will review your draft and provide feedback to help it land well with our technical audience.",
  },
];

const GUIDELINES = [
  "Submissions must meet our quality standards.",
  "Avoid big, fluff words. Keep your language simple.",
  "Articles should be at least ~1,200 words and offer value.",
  "Your content should be formatted for easy readability.",
  "You may include two self-serving links in the body or author bio.",
  "Outgoing links must be relevant but not point to competitors.",
  "Published articles cannot be republished elsewhere.",
];

function PerkCard({
  icon,
  title,
  body,
  index,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={scrollRevealViewport}
      transition={{ duration: 0.45, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="bg-card group border-foreground/8 relative overflow-hidden rounded-2xl border p-6"
    >
      <div
        className="absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% 0%, color-mix(in srgb, var(--foreground) 6%, transparent), transparent 70%)",
        }}
      />
      <div className="bg-foreground/10 text-brand mb-4 flex h-10 w-10 items-center justify-center rounded-xl">
        {icon}
      </div>
      <h3 className="text-foreground mb-2 text-base font-bold">{title}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed">{body}</p>
    </motion.div>
  );
}

function TopicPill({
  icon,
  color,
  label,
  description,
  index,
}: {
  icon: React.ReactNode;
  color: string;
  label: string;
  description: string;
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={scrollRevealViewport}
      transition={{ duration: 0.4, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
      className="border-border bg-card group hover:border-foreground/14 flex items-start gap-3 rounded-xl border px-4 py-4 transition-colors duration-200"
    >
      <div
        className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
        style={{ backgroundColor: `${color}18`, color }}
      >
        {icon}
      </div>
      <div>
        <p className="text-foreground mb-0.5 text-sm font-semibold">{label}</p>
        <p className="text-muted-foreground text-xs leading-relaxed">{description}</p>
      </div>
    </motion.div>
  );
}

export default function WriteForUsSection() {
  const prefersReducedMotion = useReducedMotion();
  const [formState, setFormState] = useState<"idle" | "sent">("idle");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [submissionGroupError, setSubmissionGroupError] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      articleTitle: "",
      articleLink: "",
      bio: "",
      socialUrl: "",
    },
    validationSchema: writeForUsSchema,
    onSubmit: (values) => {
      if (!values.articleLink && !uploadedFile) {
        setSubmissionGroupError("Please provide a Google Docs link or upload a file.");
        return;
      }
      setSubmissionGroupError(null);
      setFormState("sent");
    },
  });

  if (formState === "sent") {
    return (
      <FormSuccessPage
        title="Submission received!"
        description="Thanks for submitting your guest post. We review every submission and will get back to you within two weeks."
      >
        <Link
          href="/blog"
          className="text-foreground mt-2 inline-flex items-center gap-1.5 text-sm font-semibold transition-opacity hover:opacity-80"
        >
          Back to the blog
          <ArrowRight size={14} />
        </Link>
      </FormSuccessPage>
    );
  }

  return (
    <main id="main-content" tabIndex={-1} className="overflow-y-clip pb-32">
      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <section
        className="border-border relative overflow-y-clip border-b pt-32 pb-20"
        aria-labelledby="wfu-heading"
      >
        <HeroGlow align="center" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            {/* Badge */}
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <EyebrowBadge>Proxidize Blog — Contribute</EyebrowBadge>
            </motion.div>

            {/* Headline */}
            <motion.h1
              id="wfu-heading"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.08 }}
              className="text-foreground mb-5 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl"
            >
              Write for Proxidize
            </motion.h1>

            {/* Sub-copy */}
            <motion.p
              initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="text-muted-foreground mx-auto mb-10 max-w-xl text-lg leading-relaxed"
            >
              Share your knowledge with 50,000+ developers and data engineers. We&apos;re looking
              for practitioners who live and breathe web scraping, proxy infrastructure, and AI data
              pipelines.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.22 }}
              className="flex flex-col items-center justify-center gap-3 sm:flex-row"
            >
              <a
                href="#submit"
                className="bg-primary text-primary-foreground inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
              >
                Submit your guest post
                <ArrowRight size={15} />
              </a>
              <a
                href="#guidelines"
                className="border-border bg-card hover:bg-muted text-foreground inline-flex items-center gap-2 rounded-xl border px-6 py-3 text-sm font-semibold transition-all duration-200"
              >
                Read the guidelines
                <ChevronRight size={15} />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Why write for us ─────────────────────────────────────────────────── */}
      <section className="mx-auto mt-20 mb-24 max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="mb-10"
        >
          <h2 className="text-foreground mb-3 text-3xl font-bold sm:text-4xl">Why write for us</h2>
          <p className="text-muted-foreground max-w-lg leading-relaxed">
            Publishing with Proxidize gets your work in front of a highly technical, engaged
            audience.
          </p>
        </motion.div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PERKS.map((perk, i) => (
            <PerkCard key={perk.title} index={i} {...perk} />
          ))}
        </div>
      </section>

      {/* ── Core Writing Expectations ────────────────────────────────────────── */}
      <section className="mx-auto mb-24 max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="mb-8"
        >
          <h2 className="text-foreground mb-3 text-3xl font-bold sm:text-4xl">
            Core writing expectations
          </h2>
          <p className="text-muted-foreground max-w-lg leading-relaxed">
            We prefer depth over breadth. For example, a detailed post on &ldquo;how to scrape
            Instagram&rdquo; beats a generic &ldquo;What is Web Scraping?&rdquo; every time.
          </p>
        </motion.div>

        <div className="grid gap-2 sm:grid-cols-2 sm:gap-4">
          {WRITING_EXPECTATIONS.map((item, i) => (
            <motion.div
              key={item.text.slice(0, 40)}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={scrollRevealViewport}
              transition={{ duration: 0.4, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              className="border-border bg-card flex items-start gap-3 rounded-xl border px-5 py-4"
            >
              <div className="bg-foreground/10 text-brand mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md">
                {item.icon}
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed">{item.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Topics we cover ───────────────────────────────────────────────────── */}
      <section className="mx-auto mb-16 max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="mb-8"
        >
          <h2 className="text-foreground mb-3 text-3xl font-bold sm:text-4xl">Topics we cover</h2>
          <p className="text-muted-foreground max-w-lg leading-relaxed">
            We publish content in these areas — the more specific and hands-on, the better.
          </p>
        </motion.div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {TOPICS.map((topic, i) => (
            <TopicPill key={topic.label} index={i} {...topic} />
          ))}
        </div>
      </section>

      {/* ── Guidelines + Rejected + Process ──────────────────────────────────── */}
      <section className="mx-auto mb-24 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Submission guidelines */}
          <div id="guidelines">
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={scrollRevealViewport}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="mb-6"
            >
              <h2 className="text-foreground mb-2 text-2xl font-bold sm:text-3xl">
                Submission guidelines
              </h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Read these before you pitch — they keep the quality bar high for everyone.
              </p>
            </motion.div>

            <div className="border-border bg-card overflow-hidden rounded-2xl border">
              <ul className="divide-border divide-y">
                {GUIDELINES.map((item, i) => (
                  <motion.li
                    key={item}
                    initial={prefersReducedMotion ? false : { opacity: 0, x: 12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={scrollRevealViewport}
                    transition={{ duration: 0.38, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                    className="flex items-start gap-3 px-5 py-4"
                  >
                    <CheckCircle2 size={16} className="text-brand mt-0.5 shrink-0" />
                    <p className="text-muted-foreground text-sm leading-relaxed">{item}</p>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>

          {/* Will be rejected + Process */}
          <div className="flex flex-col gap-6">
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={scrollRevealViewport}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="mb-0"
            >
              <h2
                className="text-foreground pointer-events-none mb-2 text-2xl font-bold opacity-0 select-none sm:text-3xl"
                aria-hidden="true"
              >
                &nbsp;
              </h2>
              <p
                className="text-muted-foreground pointer-events-none text-sm leading-relaxed opacity-0 select-none"
                aria-hidden="true"
              >
                &nbsp;
              </p>
            </motion.div>

            {/* Will be rejected */}
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={scrollRevealViewport}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="rounded-2xl border border-red-500/20 bg-red-500/5 p-5"
            >
              <h3 className="mb-3 text-sm font-bold text-red-400">Will be rejected if&hellip;</h3>
              <ul className="space-y-2">
                {REJECTED_IF.map((item) => (
                  <li key={item} className="flex items-center gap-2.5">
                    <X size={13} className="shrink-0 text-red-400" />
                    <span className="text-muted-foreground text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Submission form ───────────────────────────────────────────────────── */}
      <section id="submit" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl">
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="mb-8"
          >
            <h2 className="text-foreground mb-3 text-3xl font-bold sm:text-4xl">
              Submit your guest post
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Ready to contribute? Fill in the details below and we&apos;ll review your submission
              within two weeks.
            </p>
          </motion.div>

          <motion.form
            initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.45, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            onSubmit={formik.handleSubmit}
            className="border-border bg-card space-y-5 rounded-2xl border p-6 sm:p-8"
            aria-label="Write for us submission form"
            noValidate
          >
            {/* Name + Email */}
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="wfu-name"
                  className="text-foreground mb-1.5 block text-xs font-semibold tracking-wide uppercase"
                >
                  Your name <span className="text-red-400">*</span>
                </label>
                <Input
                  id="wfu-name"
                  name="name"
                  type="text"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Jane Smith"
                  className="bg-background h-10"
                />
                {fieldTouchedError(formik, "name") && (
                  <FormError className="mt-1">{fieldTouchedError(formik, "name")}</FormError>
                )}
              </div>
              <div>
                <label
                  htmlFor="wfu-email"
                  className="text-foreground mb-1.5 block text-xs font-semibold tracking-wide uppercase"
                >
                  Email <span className="text-red-400">*</span>
                </label>
                <Input
                  id="wfu-email"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="jane@example.com"
                  className="bg-background h-10"
                />
                {fieldTouchedError(formik, "email") && (
                  <FormError className="mt-1">{fieldTouchedError(formik, "email")}</FormError>
                )}
              </div>
            </div>

            {/* Social profile */}
            <div>
              <label
                htmlFor="wfu-social"
                className="text-foreground mb-1.5 block text-xs font-semibold tracking-wide uppercase"
              >
                LinkedIn or X/Twitter profile URL <span className="text-red-400">*</span>
              </label>
              <Input
                id="wfu-social"
                name="socialUrl"
                type="url"
                value={formik.values.socialUrl}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="https://linkedin.com/in/your-profile"
                className="bg-background h-10"
              />
              {fieldTouchedError(formik, "socialUrl") && (
                <FormError className="mt-1">{fieldTouchedError(formik, "socialUrl")}</FormError>
              )}
            </div>

            {/* Author bio */}
            <div>
              <label
                htmlFor="wfu-bio"
                className="text-foreground mb-1.5 block text-xs font-semibold tracking-wide uppercase"
              >
                Author bio <span className="text-red-400">*</span>
              </label>
              <Textarea
                id="wfu-bio"
                name="bio"
                rows={3}
                value={formik.values.bio}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="2–3 sentences about yourself — your background and expertise."
                className="bg-background resize-none"
              />
              {fieldTouchedError(formik, "bio") && (
                <FormError className="mt-1">{fieldTouchedError(formik, "bio")}</FormError>
              )}
            </div>

            {/* Article title */}
            <div>
              <label
                htmlFor="wfu-article-title"
                className="text-foreground mb-1.5 block text-xs font-semibold tracking-wide uppercase"
              >
                Article title <span className="text-red-400">*</span>
              </label>
              <Input
                id="wfu-article-title"
                name="articleTitle"
                type="text"
                value={formik.values.articleTitle}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="e.g. How to Rotate Proxies with Python at Scale"
                className="bg-background h-10"
              />
              {fieldTouchedError(formik, "articleTitle") && (
                <FormError className="mt-1">{fieldTouchedError(formik, "articleTitle")}</FormError>
              )}
            </div>

            {/* Google Docs link + Attachment — at least one required */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-foreground text-xs font-semibold tracking-wide uppercase">
                  Article submission <span className="text-red-400">*</span>
                </span>
                <span className="text-muted-foreground text-xs">At least one required</span>
              </div>

              <Input
                id="wfu-article-link"
                name="articleLink"
                type="url"
                value={formik.values.articleLink}
                onChange={(e) => {
                  formik.handleChange(e);
                  if (e.target.value) setSubmissionGroupError(null);
                }}
                onBlur={formik.handleBlur}
                placeholder="Google Docs link — https://docs.google.com/document/d/..."
                className="bg-background h-10"
              />

              <div className="text-muted-foreground flex items-center gap-3 text-xs">
                <div className="bg-border h-px flex-1" />
                <span>or</span>
                <div className="bg-border h-px flex-1" />
              </div>

              <label
                htmlFor="wfu-file"
                className={`bg-background hover:bg-muted flex w-full cursor-pointer items-center gap-3 rounded-lg border border-dashed px-4 py-3 transition-colors duration-200 ${submissionGroupError && !uploadedFile && !formik.values.articleLink ? "border-red-500/50" : "border-border hover:border-foreground/30"}`}
              >
                <div className="bg-foreground/10 text-brand flex h-7 w-7 shrink-0 items-center justify-center rounded-md">
                  <Upload size={14} />
                </div>
                <div className="min-w-0 flex-1">
                  {uploadedFile ? (
                    <span className="text-foreground block truncate text-sm">
                      {uploadedFile.name}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-sm">
                      Upload a file <span className="text-xs opacity-60">— .pdf, .docx, .doc</span>
                    </span>
                  )}
                </div>
                {uploadedFile && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setUploadedFile(null);
                    }}
                    className="text-muted-foreground hover:text-foreground shrink-0 transition-colors"
                  >
                    <X size={14} />
                  </button>
                )}
              </label>
              <input
                id="wfu-file"
                type="file"
                accept=".pdf,.doc,.docx"
                className="sr-only"
                onChange={(e) => {
                  setUploadedFile(e.target.files?.[0] ?? null);
                  if (e.target.files?.[0]) setSubmissionGroupError(null);
                }}
              />

              {submissionGroupError && <FormError>{submissionGroupError}</FormError>}
            </div>

            {/* Submit */}
            <div className="flex items-center justify-between gap-4 pt-1">
              <p className="text-muted-foreground text-xs">
                We review every submission and respond within two weeks.
              </p>
              <button
                type="submit"
                className={cn(
                  brandActionClasses,
                  "inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-xl px-6 py-2.5 text-sm font-semibold transition-all duration-200 active:scale-[0.98]"
                )}
              >
                Submit post
                <Send size={14} />
              </button>
            </div>
          </motion.form>
        </div>
      </section>
    </main>
  );
}
